import { cn, focusHaloFlush } from "@pax/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { SCAN_COMPLETE_MS } from "@/features/auth/components/TurningWorld";
import { type Session, sessionKey, signOut } from "@/features/auth/queries";
import { checkSession } from "@/lib/risk";

/** How long to wait before each retry while Castle can't answer, at most the last. */
const RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000];

/** How long a check runs before it offers a way out, since a normal one lands in a second or two. */
const STUCK_MS = 8_000;

/** Whether a session is still waiting on its sign-in check, in a deployment that checks them. */
export function isCheckPending(session: Session | null | undefined, checksSessions: boolean) {
  return Boolean(session && checksSessions && !session.session.checkedAt);
}

/** Whether the reader asked for less motion, which leaves the globe unscanned. */
function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Checking a new session, on the entry screen: one quiet line in the middle of the window while
 * the globe behind it is scanned. It checks the session with Castle and an invisible bot check,
 * retrying until Castle answers. When the check lands, whether Castle allowed or denied it, the
 * scan finishes its sweep and then the session reloads so Pax opens. A check that takes much longer
 * than usual offers to sign out under the line.
 */
export function CheckingSession({ setChecked }: { setChecked: (checked: boolean) => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const router = useRouter();
  const [still] = useState(prefersReducedMotion);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setStuck(true), STUCK_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = still ? 0 : SCAN_COMPLETE_MS;
    const attempt = async (tries: number) => {
      try {
        await checkSession();
        if (!alive) return;
        setChecked(true);
        timer = setTimeout(
          () => void queryClient.invalidateQueries({ queryKey: sessionKey, exact: true }),
          finish,
        );
      } catch {
        if (!alive) return;
        const delay = RETRY_DELAYS_MS[Math.min(tries, RETRY_DELAYS_MS.length - 1)];
        timer = setTimeout(() => void attempt(tries + 1), delay);
      }
    };
    void attempt(0);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [queryClient, setChecked, still]);

  async function handleSignOut() {
    try {
      await signOut(queryClient);
    } finally {
      await navigate({ to: "/" });
      await router.invalidate();
    }
  }

  return (
    <main className="relative flex flex-1 flex-col items-center justify-center px-6">
      <h1 role="status" className="text-center text-base font-medium text-muted-foreground">
        Checking you're human
      </h1>
      {/* Below the line rather than beside it, so the line stays in the middle when it appears. */}
      {stuck && (
        <button
          type="button"
          onClick={() => void handleSignOut()}
          className={cn(
            "absolute top-1/2 mt-6 rounded text-xs text-muted-foreground animate-in duration-500 fade-in hover:text-foreground",
            focusHaloFlush,
          )}
        >
          Sign out
        </button>
      )}
    </main>
  );
}
